import { ENV } from "@/env";

/**
 * Service worker integration for session renewal.
 *
 * The worker (`public/sw.js`) owns JWT renewal: it transparently refreshes the
 * session on a 401 and replays the request, and supports proactive renewal via
 * a `SCHEDULE_REFRESH` message. This module is the page-side counterpart — it
 * registers the worker, reacts to its `SESSION_EXPIRED` / `TOKEN_REFRESHED`
 * messages, and exposes `scheduleRefresh()` for the app to call after login.
 *
 * The store dependencies (current company view, login-answer refresh) are
 * injected via `registerSwCallbacks` to avoid a circular import with the store.
 */

const SW_URL = "/sw.js";

// Injected by the app store; kept as a getter/callback pair so this module does
// not import the store directly (which would form a circular dependency).
let getCompanyView: () => string = () => "";
let onTokenRefreshed: () => void = () => {};

/** Wire up the store-backed dependencies this module needs. */
export function registerSwCallbacks(callbacks: { getCompanyView: () => string; onTokenRefreshed: () => void }) {
  getCompanyView = callbacks.getCompanyView;
  onTokenRefreshed = callbacks.onTokenRefreshed;
}

/** Redirect to SSO (or the local login page) when the session is truly gone. */
function redirectToLogin() {
  if (!ENV.SSO_DISABLED) {
    const redirectUri = `${window.location.origin}/v1/PlatformAdmin/Callback`;
    const ssoUrl = `${ENV.AUTH_SERVER}/api/authorize?client_id=${ENV.CLIENT_ID}&redirect_uri=${redirectUri}&response_type=code&scope=openid`;
    window.location.replace(ssoUrl);
  } else {
    window.location.replace("/login");
  }
}

/** Decode a JWT's `exp` claim and return it as a ms-epoch timestamp, or null. */
export function getTokenExpiry(token?: string | null): number | null {
  if (!token) return null;
  const payload = token.split(".")[1];
  if (!payload) return null;
  try {
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = JSON.parse(atob(base64)) as { exp?: number };
    return typeof json.exp === "number" ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

function handleMessage(event: MessageEvent) {
  const data = (event.data ?? {}) as { type?: string };

  switch (data.type) {
    case "SESSION_EXPIRED":
      redirectToLogin();
      break;
    case "TOKEN_REFRESHED":
      // The cookie was renewed but our cached login answer (and its token
      // expiry) is now stale. Let the app refresh it and re-arm proactive
      // renewal so the session stays alive for the next cycle.
      onTokenRefreshed();
      break;
    default:
      break;
  }
}

/** Register the service worker and wire up its message channel. */
export function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return;

  navigator.serviceWorker.addEventListener("message", handleMessage);

  window.addEventListener("load", () => {
    navigator.serviceWorker.register(SW_URL).catch((error) => {
      console.warn("Service worker registration failed; falling back to reactive auth handling.", error);
    });
  });
}

/**
 * Ask the worker to proactively renew the session shortly before `expiresAt`
 * (ms epoch). Safe to call before the worker is controlling the page — it
 * waits for the active registration. No-op when service workers are absent.
 */
export function scheduleRefresh(expiresAt: number) {
  if (!("serviceWorker" in navigator)) return;

  const companyView = getCompanyView();

  void navigator.serviceWorker.ready.then((registration) => {
    registration.active?.postMessage({
      type: "SCHEDULE_REFRESH",
      expiresAt,
      companyView,
    });
  });
}
